import React, { useState, useEffect, useRef, useCallback, Suspense, useImperativeHandle, forwardRef } from "react";
import { ChangeSet, Location, ObjectState, PrimitiveState, State, TrackedArray, TrackedPrimitive, TrackedRecord, TrackedObject, getCompressedEvents, getWinningOperation, Author, getTrackedObjectBreadcrumbPath, getPositionInParent } from "@aidkitorg/typesheets/lib/collab";
import { stringToColor } from "../Util";
import { MIGRATIONS } from "../Migrations";

export function CollabBreadcrumb({ path, position }: {
  path: Array<{ kind: string, label: string }>,
  position?: number,
}) {
  if (!path.length && position === undefined) return null;
  return (
    <div className="flex items-center gap-1 flex-wrap text-xs">
      {path.map((seg, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="text-gray-300 select-none">›</span>}
          <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 bg-gray-100">
            <span className="rounded-full px-1.5 py-0.5 font-medium text-gray-800"
              style={{ backgroundColor: stringToColor(seg.kind, '70%') }}>
              {seg.kind}
            </span>
            {seg.label && <span className="text-gray-500">{seg.label}</span>}
          </span>
        </React.Fragment>
      ))}
      {position !== undefined && (
        <>
          {path.length > 0 && <span className="text-gray-300 select-none">›</span>}
          <span className="inline-flex items-center rounded px-1.5 py-0.5 bg-gray-100 text-gray-500">
            position {position}
          </span>
        </>
      )}
    </div>
  );
}

const Distro = React.lazy(() => import("@aidkitorg/typesheets/lib/distroeditor"));

/**
 * Error boundary that catches Distro rendering crashes and shows a fallback.
 */
class DistroErrorBoundary extends React.Component<
  { children: React.ReactNode, fallback?: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode, fallback?: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error) {
    console.warn('Distro render error caught by boundary:', error.message);
  }
  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? (
        <div className="p-4 text-sm text-gray-500 bg-gray-50 rounded border border-gray-200">
          Preview unavailable
        </div>
      );
    }
    return this.props.children;
  }
}

export const CollectionComponentKinds = new Set([
  'Section',
  'Collection',
  'Subsurvey',
  'Templated Block',
  'Payment',
  'Notification',
  'Usio Mailing',
  'GiveCard Mailing',
  'USBank Card Mailing',
  'Dashboard',
  'Applicant Identities',
  'Applicant Portal Page',
  'AI Assistant Review Survey',
  'Macro'
]);

export const SectionComponentKinds = new Set([
  'Action',
  'Address',
  'Applicant Creator',
  'ArcGIS Attachment Viewer',
  'Assignee',
  'Attachment',
  'Bank Routing Number',
  'Benefits Calculator',
  'Card Management',
  'Consent',
  'Computed',
  'Confirmation',
  'Contact Confirmation',
  'Date',
  'Deck Verification',
  'Encrypted Value Verification',
  'Explanation',
  'Household Calculator',
  'Fillable Form',
  'Flag',
  'Duplicate Review',
  'Income Calculator',
  'Inline Language Selector',
  'Inline Notification',
  'Inline Signature',
  'Likert',
  'Liveness Detection',
  'Login Widget',
  'Lookup',
  'Number',
  'Number With Unit',
  'Plaid Bank Account',
  'Plaid Income',
  'Ranking Question',
  'Resume Widget',
  'Search Select',
  'Section',
  'Select',
  'Select From Data',
  'Show Field',
  'Similar Document Check',
  'Similar Document Review',
  'Submit Button',
  'Subsurvey',
  'Support Button',
  'Text Entry',
  'Third Party Check',
  'Validated',
  'Milestone',
  'Related Applicants',
  'Image Preview',
  'Conditional Block',
  'Templated Block',
  'State Selector',
  'County Selector',
  'Country Selector',
]);

export const OtherComponentKinds = new Set([
  'Persona',
  'Comms Config',
  'User',
  'User Group',
  'Fraud Flag',
  'Cross Program Data Exchange',
  'Geo Data Sync',
  'Root Tab'
]);

export { getTrackedObjectBreadcrumbPath, getPositionInParent } from "@aidkitorg/typesheets/lib/collab";

/**
 * Traces the parent chain from `obj` up to the object with id `parentId`,
 * collecting property keys for TrackedRecord parents and index segments for
 * TrackedArray parents.
 * Returns the field path segments, e.g. ['name', 'en'] for a primitive
 * stored at parent.name.en.
 * Returns [] when obj.id === parentId (the object is the renderable parent itself).
 */
export function getFieldPath(obj: TrackedObject, parentId: string): string[] {
  const path: string[] = [];
  let current: TrackedObject | undefined = obj;
  while (current && current.id !== parentId) {
    const currentId = current.id;
    const parent: TrackedObject | undefined = current.parent;
    if (parent instanceof TrackedRecord) {
      const key = Object.keys(parent.value).find(k => parent.value[k]?.id === currentId);
      if (key) path.unshift(key);
    } else if (parent instanceof TrackedArray) {
      const index = parent.value.findIndex(item => item.id === currentId);
      if (index >= 0) path.unshift(String(index));
    }
    current = parent;
  }
  return path;
}

export function isRenderableKind(kind: string): boolean {
  return CollectionComponentKinds.has(kind) || SectionComponentKinds.has(kind) || OtherComponentKinds.has(kind);
}

/**
 * Returns true if the object has an unbroken parent chain leading to root.
 * Objects in orphaned subtrees (moved into a container that was later deleted)
 * have a parent reference in workingSet but are not reachable from root.
 */
export function isFullyAlive(obj: TrackedObject | undefined): boolean {
  if (!obj) return false;
  if (obj.id === 'root') return true;
  return isFullyAlive(obj.parent);
}


export function findRenderableParent(obj: TrackedObject | undefined, parentState?: State): TrackedObject | undefined {
  if (!obj) return undefined;
  const kind = obj?.read()?.kind ?? parentState?.workingSet[obj.id]?.read()?.kind;
  if (kind && isRenderableKind(kind)) {
    return obj;
  }

  // root is a renderable parent via the Root Tab.
  if (obj.id === 'root') {
    return obj;
  }

  return findRenderableParent(obj.parent, parentState);
}

/**
 * Builds a compressed ChangeSet directly from a State's objects without calling
 * getCompressedEvents (which runs ReconstructState and can corrupt pre-built states).
 */
function buildEventsFromState(state: State): ChangeSet {
  const events: ChangeSet = [];
  for (const id in state.objects) {
    const obj = state.objects[id];
    // Always emit the location event (even if undefined) — matches getCompressedEvents behavior.
    // reconstructStateFromEvents needs this to know the object exists.
    const latestLoc = getWinningOperation(obj.locations);
    events.push([id, obj.kind, latestLoc!]);
    if (obj.kind === 'primitive') {
      const latestValue = getWinningOperation((obj as PrimitiveState).versions);
      events.push([id, obj.kind, latestValue!]);
    }
  }
  return events;
}

/**
 * Deep copies a TrackedObject into a new isolated State, so edits don't affect the original.
 */
export function deepCopyIntoState(obj: TrackedObject): { state: State, root: TrackedObject, events: ChangeSet } {
  const workingSet: Record<string, TrackedObject> = {};
  const objects: Record<string, ObjectState | PrimitiveState> = {};
  const state: State = {
    objects,
    workingSet,
    currentRevision: 0,
    changeSetOpen: 0,
    undoStack: [],
    redoStack: [],
  };

  const deepCopyObject = (o: TrackedObject): TrackedObject => {
    const orig = o.state.objects[o.id];
    // Deep-copy locations/versions arrays so mutations (e.g. TrackedRecord.set)
    // don't corrupt the original state's operation history.
    objects[o.id] = orig.kind === 'primitive'
      ? { ...orig, locations: [...orig.locations], versions: [...orig.versions] }
      : { ...orig, locations: [...orig.locations] };

    if (o.state.objects[o.id].kind === 'array' && Array.isArray(o.value)) {
      workingSet[o.id] = new TrackedArray(state, o.id, o.value.map(v => deepCopyObject(v)));
    } else if (o.state.objects[o.id].kind === 'record' && typeof o.value === 'object') {
      workingSet[o.id] = new TrackedRecord(state, o.id, Object.keys(o.value).reduce((acc, k) => {
        acc[k] = deepCopyObject(o.value[k] as TrackedObject);
        return acc;
      }, {} as Record<string, TrackedObject>));
    } else {
      workingSet[o.id] = new TrackedPrimitive(state, o.id, o.read());
    }

    return workingSet[o.id];
  };

  const root = deepCopyObject(obj);
  const events = buildEventsFromState(state);
  return { state, root, events };
}

/**
 * Normalizes events so the given object becomes the root, which Distro requires.
 */
function normalizeToRoot(objId: string, events: ChangeSet, changes?: RenderChanges): { events: ChangeSet, changes?: RenderChanges } {
  if (objId === 'root') return { events, changes };

  const normalizedEvents = events.map(event => {
    if (event[0] === objId) {
      return ['root', event[1], event[2]] as ChangeSet[0];
    } else if (event[2]?.op === 'move' && event[2].loc?.id === objId) {
      return [event[0], event[1], { ...event[2], loc: { ...event[2].loc, id: 'root' } }] as ChangeSet[0];
    }
    return event;
  });

  const normalizedChanges = changes ? {
    additions: changes.additions.map(a => ({
      ...a,
      object: {
        ...a.object,
        id: a.object.id === objId ? 'root' : a.object.id
      }
    } as { object: TrackedObject, location?: Location })),
    removals: changes.removals.map(r => ({
      ...r,
      object: {
        ...r.object,
        id: r.object.id === objId ? 'root' : r.object.id
      }
    } as { object: TrackedObject, location?: Location })),
  } : undefined;

  return { events: normalizedEvents, changes: normalizedChanges };
}

type RenderChanges = {
  additions: {
    object: TrackedObject,
    location?: Location,
    authors?: Author[]
  }[],
  removals: {
    object: TrackedObject,
    location?: Location,
    authors?: Author[]
  }[]
};

export type EditableDistroHandle = {
  getEditedEvents: () => ChangeSet | undefined;
};

/**
 * Renders a TrackedObject as a Distro component with optional change highlights.
 * When editable, passes a real onChange and exposes edited state via ref.
 *
 * @param object - The TrackedObject to render. If undefined, renders an error placeholder.
 * @param changes - Optional additions/removals to highlight within the rendered component.
 * @param parentIsRoot - When true, wraps the object in a synthetic Root Tab so Distro can render
 *   objects that don't have their own renderable kind (e.g. program config, users tab).
 * @param editable - When true, the Distro editor is interactive and user changes are captured.
 *   The ref's `getEditedEvents` method is only meaningful when this is true.
 * @param onEditStateChange - Called with `true` when the user makes their first edit.
 *   Use this to show/hide edit-related UI (e.g. a Reset button).
 */
export const RenderDistro = forwardRef<EditableDistroHandle, {
  object: TrackedObject | undefined,
  changes?: RenderChanges,
  parentIsRoot: boolean,
  editable?: boolean,
  onEditStateChange?: (hasEdits: boolean) => void,
}>((props, ref) => {
      const distroRef = useRef<React.ComponentRef<typeof Distro> | null>(null);
      const userEventsRef = useRef<ChangeSet>([]);
      const initializedRef = useRef(false);

      const [events, setEvents] = useState<ChangeSet | undefined>();
      const [root, setRoot] = useState<TrackedObject | undefined>();
      const [rootKind, setRootKind] = useState<string | undefined>();
      const [initializedKey, setInitializedKey] = useState<string | undefined>();
      const [distroMounted, setDistroMounted] = useState(false);
      const [changesToRender, setChangesToRender] = useState<RenderChanges | undefined>(props.changes);

      // Keep a ref to the latest onEditStateChange so the Distro onChange callback
      // (which Distro caches from its first render) always calls the current version.
      const onEditStateChangeRef = useRef(props.onEditStateChange);
      onEditStateChangeRef.current = props.onEditStateChange;

      const handleChange = props.editable
        ? (_value: any, _state: State, changeEvents: ChangeSet) => {
          if (initializedRef.current) {
            userEventsRef.current.push(...changeEvents);
            onEditStateChangeRef.current?.(true);
          } else {
            initializedRef.current = true;
          }
        }
        : () => {};

      // Callback ref to detect when the lazy-loaded Distro component mounts
      const distroCallbackRef = useCallback((node: React.ComponentRef<typeof Distro> | null) => {
        distroRef.current = node;
        if (node) setDistroMounted(true);
      }, []);

      useImperativeHandle(ref, () => ({
        getEditedEvents: () => {
          if (!userEventsRef.current.length) return undefined;
          return userEventsRef.current;
        },
      }));

      if (!props.object) {
        return <div>Unknown object</div>
      }

      useEffect(() => {
        if (!props.object) return;

        const cacheKey = props.editable
          ? props.object.id
          : props.object.id + (props.changes?.additions.length || 0) + (props.changes?.removals.length || 0);
        if (initializedKey === cacheKey) return;

        const { state, root: newObj, events: rawEvents } = deepCopyIntoState(props.object);
        // These cover special cases that don't have a kind, like the users or program config tabs.
        if (props.parentIsRoot) {
          const newRoot = new TrackedRecord(newObj.state, 'root', {
            kind: new TrackedPrimitive(newObj.state, undefined, 'Root Tab'),
          });
          newRoot.set('changedObject', newObj);
          const wrappedEvents = buildEventsFromState(newRoot.state);
          setRoot(newRoot);
          setRootKind(newRoot.read().kind);
          setEvents(wrappedEvents);
          setChangesToRender(props.changes);
          setInitializedKey(cacheKey);
          return;
        }
        const { events: normalizedEvents, changes: normalizedChanges } = normalizeToRoot(newObj.id, rawEvents, props.changes);
        if (newObj.id !== 'root') {
          newObj.id = 'root';
        }

        setRoot(newObj);
        setRootKind(newObj.read().kind);
        setEvents(normalizedEvents);
        setChangesToRender(normalizedChanges || props.changes);
        setInitializedKey(cacheKey);
      }, [props, initializedKey]);

      useEffect(() => {
        if (!root || !events || !distroRef.current) return;

        distroRef.current?.initialize(root.read(), undefined, events, undefined, undefined, undefined, undefined, { changesToRender });
      }, [root, events, changesToRender, distroMounted]);

      if (rootKind && isRenderableKind(rootKind)) {
        return <div className={`mt-4 bg-white md:rounded-lg${props.editable ? ' border-2 border-blue-300' : ''}`}>
          <DistroErrorBoundary>
            <Suspense>
              <Distro
                ref={distroCallbackRef}
                types='src/survey.ts'
                name={rootKind.replace(/\s+/g, '')}
                onChange={handleChange}
                migrations={MIGRATIONS}
                readonly={!props.editable}
              />
            </Suspense>
          </DistroErrorBoundary>
        </div>
      }

      return <div><div>Unable to render: {JSON.stringify(props.object?.read())}</div></div>
    });

/**
 * Renders a breadcrumb path for a tracked object, falling back to parentState
 * for deleted objects that no longer have a parent in the current state.
 */
export function ChangeBreadcrumb({ obj, isRoot, objId, parentState }: {
  obj: TrackedObject | undefined,
  isRoot: boolean,
  objId: string,
  parentState?: State,
}) {
  if ((!obj?.parent || !isFullyAlive(obj)) && parentState) {
    const parentObj = parentState.workingSet[objId];
    if (parentObj) {
      const path = getTrackedObjectBreadcrumbPath(parentObj);
      const position = getPositionInParent(parentObj);
      if (path.length || position !== undefined) {
        return <CollabBreadcrumb path={path} position={position} />;
      }
    }
  }
  const path = getTrackedObjectBreadcrumbPath(obj);
  const position = getPositionInParent(obj);
  if (!path.length && position === undefined) {
    return <span className="text-gray-400">{isRoot ? 'root' : objId.substring(0, 8)}</span>;
  }
  return <CollabBreadcrumb path={path} position={position} />;
}
